"use client"

import { useEffect, useState } from "react"
import { Button, Card, Form, Table } from "react-bootstrap"
import { useTranslation } from "../../hooks/use-translation"
import { useAppDispatch, useAppSelector } from "../../redux/hooks"
import { fetchRooms } from "../../redux/slices/roomsSlice"
import { fetchBuildings } from "../../redux/slices/buildingsSlice"
import { PlusButton } from "../../components/ui/plus-button"

export default function AvailableRoomsPage() {
  const dispatch = useAppDispatch()
  const { user } = useAppSelector((state) => state.auth)
  const { rooms = [], isLoading } = useAppSelector((state) => state.rooms)
  const { buildings = [] } = useAppSelector((state) => state.buildings)
  const { t } = useTranslation()
  const isAdmin = user?.role === "admin"
  const [selectedBuilding, setSelectedBuilding] = useState<string>("")
  const [selectedType, setSelectedType] = useState<string>("")

  // Fetch rooms when component mounts or filters change
  useEffect(() => {
    dispatch(
      fetchRooms({
        building: selectedBuilding !== "all" ? selectedBuilding : undefined,
        type: selectedType !== "all" ? selectedType : undefined,
      }),
    )
    dispatch(fetchBuildings(false)) // Only fetch active buildings
  }, [dispatch, selectedBuilding, selectedType])

  const filteredRooms = rooms
    ? rooms.filter((room) => {
        if (selectedBuilding && selectedBuilding !== "all" && room.building !== selectedBuilding) return false
        if (selectedType && selectedType !== "all" && room.type !== selectedType) return false
        return true
      })
    : []

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="py-3">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
        <h1 className="fs-2 fw-bold mb-0 text-white">{t("availableRooms")}</h1>
        {isAdmin && <PlusButton to="/dashboard/available-rooms/new" title={t("addRoom")} />}
      </div>

      <Card className="mb-4">
        <Card.Header>
          <Card.Title>{t("availableApartments")}</Card.Title>
          <Card.Text className="text-muted small">{t("availableApartmentsDescription")}</Card.Text>
        </Card.Header>
        <Card.Body>
          <div className="mb-4 d-flex flex-column flex-sm-row gap-3">
            <Form.Group style={{ width: "200px" }}>
              <Form.Select value={selectedBuilding} onChange={(e) => setSelectedBuilding(e.target.value)}>
                <option value="all">{t("allBuildings")}</option>
                {buildings.map((building) => (
                  <option key={building.id} value={building.id}>
                    {building.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group style={{ width: "200px" }}>
              <Form.Select value={selectedType} onChange={(e) => setSelectedType(e.target.value)}>
                <option value="all">{t("allTypes")}</option>
                <option value="studio">{t("studio")}</option>
                <option value="1-bedroom">{t("oneBedroom")}</option>
                <option value="2-bedroom">{t("twoBedroom")}</option>
              </Form.Select>
            </Form.Group>
          </div>

          <div className="table-responsive">
            <Table bordered hover>
              <thead>
                <tr>
                  <th>{t("building")}</th>
                  <th>{t("roomNumber")}</th>
                  <th>{t("type")}</th>
                  <th>{t("size")}</th>
                  <th>{t("price")}</th>
                  <th>{t("availableFrom")}</th>
                  <th className="text-end">{t("actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filteredRooms.map((room) => (
                  <tr key={room.id}>
                    <td>{buildings.find((b) => b.id === room.building)?.name || room.building}</td>
                    <td>{room.number}</td>
                    <td>
                      {room.type === "studio"
                        ? t("studio")
                        : room.type === "1-bedroom"
                          ? t("oneBedroom")
                          : t("twoBedroom")}
                    </td>
                    <td>{room.size}</td>
                    <td>${room.price}/month</td>
                    <td>{new Date(room.available).toLocaleDateString("el-GR")}</td>
                    <td className="text-end">
                      <Button variant="outline-primary" size="sm">
                        {t("inquire")}
                      </Button>
                    </td>
                  </tr>
                ))}
                {filteredRooms.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center py-4 text-muted">
                      {t("noRoomsFound")}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
    </div>
  )
}
